import { AlertCircle, AlertTriangle, Clock, Info, type LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/ui/Badge';
import type {
  HintCode,
  HintSeverity,
  Integration,
  IntegrationHint,
} from '../types/integrations';

export interface HintPresentation {
  title: string;
  remediation: string;
  icon: LucideIcon;
}

/**
 * Human-friendly copy and icons per hint code. The backend `message` stays as the
 * body text; this table adds structure (title, remediation, icon) that the backend
 * doesn't know about.
 */
export const HINT_PRESENTATION: Record<HintCode, HintPresentation> = {
  'content-type-form-encoded': {
    title: 'Form-encoded payloads',
    remediation:
      'Configure the provider to send webhooks as application/json for best compatibility.',
    icon: AlertTriangle,
  },
  'signature-failures': {
    title: 'Signature verification failing',
    remediation:
      'Check that the webhook secret matches the one configured on the provider. Rotating may help.',
    icon: AlertCircle,
  },
  'high-error-rate': {
    title: 'High error rate',
    remediation:
      'Recent deliveries are failing more often than normal. Inspect the deliveries log below.',
    icon: AlertCircle,
  },
  stale: {
    title: 'No recent events',
    remediation:
      'This integration has not received events in a while. Confirm the provider is still configured.',
    icon: Clock,
  },
};

/** Stable iteration order for rendering multiple hints. */
const HINT_CODE_ORDER: HintCode[] = [
  'content-type-form-encoded',
  'signature-failures',
  'high-error-rate',
  'stale',
];

const SEVERITY_RANK: Record<HintSeverity, number> = {
  info: 0,
  warning: 1,
  error: 2,
};

export function hasHints(integration: Integration): boolean {
  return getHintEntries(integration).length > 0;
}

export function getHintEntries(
  integration: Integration,
): Array<[HintCode, IntegrationHint]> {
  const hints = integration.hints;
  if (!hints) return [];
  const entries: Array<[HintCode, IntegrationHint]> = [];
  for (const code of HINT_CODE_ORDER) {
    const hint = hints[code];
    if (hint) entries.push([code, hint]);
  }
  return entries;
}

export function highestSeverity(integration: Integration): HintSeverity | null {
  const entries = getHintEntries(integration);
  if (entries.length === 0) return null;
  return entries.reduce<HintSeverity>((acc, [, h]) => {
    return SEVERITY_RANK[h.severity] > SEVERITY_RANK[acc] ? h.severity : acc;
  }, 'info');
}

export function severityToBadgeVariant(severity: HintSeverity): BadgeVariant {
  switch (severity) {
    case 'info':
      return 'info';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
  }
}

/** Default icon fallback if an unknown HintCode sneaks through. */
export function iconForHint(code: HintCode, severity: HintSeverity): LucideIcon {
  return HINT_PRESENTATION[code]?.icon ?? (severity === 'info' ? Info : AlertTriangle);
}
